import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { FileText, Sparkles, Download, RefreshCcw, Check, Archive, FileType, Presentation as PresentationIcon } from "lucide-react";
import { useAuth } from "../lib/auth";

const DRAFT_ROLES = new Set(["esg_lead", "tenant_admin", "approver", "kpi_owner", "platform_admin"]);
const GENERATE_ROLES = new Set(["esg_lead", "tenant_admin", "approver", "platform_admin"]);

export default function Reports() {
  const { user } = useAuth();
  const canDraft = DRAFT_ROLES.has(user?.role);
  const canGenerate = GENERATE_ROLES.has(user?.role);
  const [templates, setTemplates] = useState([]);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState("");
  const [selected, setSelected] = useState(null);
  const [draftingIdx, setDraftingIdx] = useState(-1);
  const [draftErr, setDraftErr] = useState("");

  const load = async () => {
    const [t, r] = await Promise.all([api.get("/reports/templates"), api.get("/reports")]);
    setTemplates(t.data.templates); setReports(r.data);
  };
  useEffect(() => { load(); }, []);

  const generate = async (tpl) => {
    setBusy(tpl.id);
    try {
      const r = await api.post(`/reports/${tpl.id}/generate`);
      setSelected(r.data);
      await load();
    } finally { setBusy(""); }
  };

  const draftSection = async (idx) => {
    if (!selected) return;
    setDraftingIdx(idx); setDraftErr("");
    try {
      const r = await api.post(`/reports/${selected.id}/sections/draft`, { section_index: idx });
      const next = { ...selected };
      next.sections = [...selected.sections];
      next.sections[idx] = r.data.section;
      setSelected(next);
      load();
    } catch (e) {
      setDraftErr(e?.response?.data?.detail || "Drafting failed");
    } finally { setDraftingIdx(-1); }
  };

  const downloadMarkdown = (rep) => {
    let md = `# ${templates.find(t=>t.id===rep.template_id)?.name || rep.template_id}\n`;
    md += `_Generated ${rep.generated_at} by ${rep.generated_by}_\n\n`;
    for (const s of rep.sections) {
      md += `## ${s.title}${s.ai_drafted ? " ✦" : ""}\n\n${s.body}\n\n`;
    }
    const blob = new Blob([md], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${rep.template_id}_${rep.id.slice(0,8)}.md`;
    a.click();
  };

  const [packing, setPacking] = useState("");
  const [exporting, setExporting] = useState("");
  const exportFullKpi = async (format, pptxMode) => {
    const key = format + (pptxMode ? `-${pptxMode}` : "");
    setExporting(key);
    try {
      let url = "/reports/full-kpi/pdf";
      let filename = "ESG_Full_KPI_Report.pdf";
      if (format === "pptx") {
        url = `/reports/full-kpi/pptx?mode=${pptxMode}`;
        filename = `ESG_Full_KPI_${pptxMode}.pptx`;
      }
      const r = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (e) {
      alert(e?.response?.data?.detail || `Failed to export ${format.toUpperCase()}`);
    } finally { setExporting(""); }
  };

  const downloadAssurancePack = async (rep) => {
    setPacking(rep.id);
    try {
      const r = await api.get(`/reports/${rep.id}/assurance-pack`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `assurance_pack_${rep.id.slice(0,8)}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to build assurance pack");
    } finally { setPacking(""); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="reports-root">
      <div>
        <div className="section-label">Disclosure</div>
        <h1 className="text-2xl font-bold tracking-tight mt-1">ESG Reports</h1>
        <p className="text-sm text-ink-soft">Generate standard-aligned reports from approved measurements. Use <span className="font-mono">✦ Draft with AI</span> per section to produce audit-grade prose.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {templates.map(t => (
          <div key={t.id} className="card p-4" data-testid={`tpl-${t.id}`}>
            <div className="h-8 w-8 rounded-md bg-brand text-white flex items-center justify-center mb-3"><FileText size={14}/></div>
            <div className="font-semibold text-sm">{t.name}</div>
            <p className="text-xs text-ink-soft mt-1 min-h-[42px]">{t.description}</p>
            <button
              data-testid={`generate-${t.id}`}
              disabled={busy === t.id || !canGenerate}
              onClick={() => generate(t)}
              className="btn btn-primary mt-3 w-full justify-center"
              title={canGenerate ? "Generate this report" : "Read-only role — contact your ESG Lead"}
            >
              <Sparkles size={12}/> {busy === t.id ? "Generating…" : (canGenerate ? "Generate" : "Read-only")}
            </button>
          </div>
        ))}
      </div>

      {/* Full KPI Report — PDF / PPTX exports */}
      <div className="card p-5 bg-gradient-to-br from-emerald-50/50 to-white border-emerald-200/60"
           data-testid="full-kpi-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-2xl">
            <div className="section-label">Full KPI Report</div>
            <h2 className="text-lg font-semibold tracking-tight mt-1 flex items-center gap-2">
              <FileType size={18} className="text-brand"/>
              Visual, branded export · every registered KPI
            </h2>
            <p className="text-sm text-ink-soft mt-1">
              One download that captures every KPI in your catalogue with its current measure,
              organisational target, owner, trend chart and standards alignment. Choose a polished
              PDF for auditors and execs, or a deck for board-room presentations.
            </p>
            <div className="mt-2 flex flex-wrap gap-1 text-[10px] text-ink-soft">
              <span className="pill">All material issues</span>
              <span className="pill">Trend charts</span>
              <span className="pill">Owner & target</span>
              <span className="pill">IFRS S2 · GRI · SASB · GHG · ISO</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 min-w-[260px]">
            <button
              data-testid="export-pdf"
              disabled={exporting === "pdf"}
              onClick={() => exportFullKpi("pdf")}
              className="btn btn-primary w-full justify-center"
            >
              {exporting === "pdf" ? (
                <><RefreshCcw size={12} className="animate-spin"/> Building PDF…</>
              ) : (
                <><FileType size={12}/> Download branded PDF</>
              )}
            </button>
            <button
              data-testid="export-pptx-exec"
              disabled={exporting === "pptx-executive"}
              onClick={() => exportFullKpi("pptx", "executive")}
              className="btn btn-secondary w-full justify-center"
            >
              {exporting === "pptx-executive" ? (
                <><RefreshCcw size={12} className="animate-spin"/> Building deck…</>
              ) : (
                <><PresentationIcon size={12}/> Executive PPTX</>
              )}
            </button>
            <button
              data-testid="export-pptx-comp"
              disabled={exporting === "pptx-comprehensive"}
              onClick={() => exportFullKpi("pptx", "comprehensive")}
              className="btn btn-secondary w-full justify-center"
            >
              {exporting === "pptx-comprehensive" ? (
                <><RefreshCcw size={12} className="animate-spin"/> Building deck…</>
              ) : (
                <><PresentationIcon size={12}/> Comprehensive PPTX</>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border section-label">Previously generated</div>
        <table className="dense">
          <thead><tr><th>Template</th><th>Generated</th><th>By</th><th>AI sections</th><th></th></tr></thead>
          <tbody>
            {reports.map(r => {
              const ai = (r.sections || []).filter(s => s.ai_drafted).length;
              return (
                <tr key={r.id} data-testid={`report-${r.id}`}>
                  <td className="font-medium">{templates.find(t=>t.id===r.template_id)?.name || r.template_id}</td>
                  <td className="text-xs text-ink-soft font-mono">{r.generated_at}</td>
                  <td className="text-xs">{r.generated_by}</td>
                  <td>{ai > 0 ? <span className="pill pill-info">✦ {ai}</span> : <span className="pill">—</span>}</td>
                  <td className="text-right">
                    <button data-testid={`view-${r.id}`} onClick={()=>setSelected(r)} className="btn btn-ghost">View</button>
                    <button data-testid={`download-${r.id}`} onClick={()=>downloadMarkdown(r)} className="btn btn-secondary"><Download size={12}/> .md</button>
                    <button
                      data-testid={`assurance-${r.id}`}
                      onClick={()=>downloadAssurancePack(r)}
                      disabled={packing === r.id}
                      className="btn btn-secondary"
                      title="Download audit-grade ZIP (report + evidence + audit log + SHA256 manifest)"
                    >
                      {packing === r.id ? <RefreshCcw size={12} className="animate-spin"/> : <Archive size={12}/>}
                      {packing === r.id ? " Packing…" : " Assurance"}
                    </button>
                  </td>
                </tr>
              );
            })}
            {reports.length===0 && <tr><td colSpan={5} className="text-center text-ink-soft py-6">No reports generated yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-6" onClick={()=>setSelected(null)} data-testid="report-modal">
          <div className="bg-white card max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 sticky top-0 bg-white pb-2 border-b border-border">
              <div>
                <div className="section-label">Report</div>
                <h2 className="text-xl font-semibold">{templates.find(t=>t.id===selected.template_id)?.name}</h2>
                <div className="text-xs text-ink-soft">{selected.generated_at} · by {selected.generated_by}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid="download-current" onClick={()=>downloadMarkdown(selected)} className="btn btn-secondary"><Download size={12}/> Download .md</button>
                <button
                  data-testid="assurance-current"
                  onClick={()=>downloadAssurancePack(selected)}
                  disabled={packing === selected.id}
                  className="btn btn-primary"
                  title="Audit-grade ZIP — report + linked evidence + audit excerpts + SHA256 manifest"
                >
                  {packing === selected.id ? <><RefreshCcw size={12} className="animate-spin"/> Packing…</> : <><Archive size={12}/> Assurance Pack</>}
                </button>
                <button data-testid="close-report" onClick={()=>setSelected(null)} className="btn btn-ghost">Close</button>
              </div>
            </div>
            {draftErr && <div className="text-status-error text-sm mb-3">{draftErr}</div>}
            {selected.sections.map((s, i) => (
              <div key={i} className="mb-5 border-b border-border pb-4 last:border-b-0" data-testid={`report-section-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="section-label">{s.title}</div>
                    {s.ai_drafted && <span className="pill pill-info text-[10px]" title={`Drafted ${s.drafted_at}`}>✦ AI drafted</span>}
                  </div>
                  {canDraft && (
                    <button
                      data-testid={`draft-section-${i}`}
                      onClick={() => draftSection(i)}
                      disabled={draftingIdx === i}
                      className="btn btn-secondary"
                      title="Use Claude to rewrite this section using tenant data"
                    >
                      {draftingIdx === i ? (
                        <><RefreshCcw size={12} className="animate-spin"/> Drafting…</>
                      ) : s.ai_drafted ? (
                        <><Sparkles size={12}/> Re-draft</>
                      ) : (
                        <><Sparkles size={12}/> Draft with AI</>
                      )}
                    </button>
                  )}
                </div>
                <div className="text-sm whitespace-pre-wrap leading-relaxed text-ink">{s.body}</div>
                {s.ai_drafted && (
                  <div className="text-[11px] text-ink-soft mt-2 flex items-center gap-1">
                    <Check size={11}/> Drafted by {s.drafted_by} · {s.drafted_at}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
